/**
 * SystemRegistry - Registry for game systems with dependency management.
 *
 * Game systems contain logic that processes game state. They can:
 * - Initialize when the game starts
 * - Process game ticks (for idle progression)
 * - React to events
 * - Clean up when disposed
 */

import type { ContentRegistry } from './ContentRegistry';
import type { EventBus } from './EventBus';
import type { UIRegistry } from './UIRegistry';

/**
 * Context provided to game systems.
 */
export interface GameContext {
  /** Content registry for accessing game data */
  content: ContentRegistry;
  /** System registry for inter-system communication */
  systems: SystemRegistry;
  /** Event bus for pub/sub communication */
  events: EventBus;
  /** UI registry for component injection */
  ui: UIRegistry;
}

/**
 * Interface for game systems.
 *
 * Systems are registered by content packs and provide game logic.
 */
export interface GameSystem {
  /** Unique identifier for this system */
  id: string;

  /** Other system IDs that must be initialized first */
  dependencies?: string[];

  /**
   * Called once when the system initializes.
   * Use this to set up event listeners, load data, etc.
   */
  initialize?(context: GameContext): void | Promise<void>;

  /**
   * Called every game tick.
   * Use this for idle progression, timers, etc.
   *
   * @param context - Game context
   * @param deltaMilliseconds - Time since last tick in milliseconds
   */
  tick?(context: GameContext, deltaMilliseconds: number): void;

  /**
   * Called when the system is disposed.
   * Use this to clean up event listeners, etc.
   */
  dispose?(): void;
}

/**
 * SystemRegistry manages game systems.
 *
 * @example
 * ```ts
 * const registry = new SystemRegistry();
 *
 * registry.register({
 *   id: 'experience',
 *   dependencies: ['combat'],
 *   initialize(context) {
 *     context.events.on('combat:kill', this.onKill);
 *   },
 *   tick(context, delta) {
 *     // Process idle XP gain
 *   }
 * });
 *
 * await registry.initializeAll(gameContext);
 * registry.tick(gameContext, 16); // ~60fps
 * ```
 */
export class SystemRegistry {
  /** Registered systems */
  private systems = new Map<string, GameSystem>();

  /** Systems in initialization order */
  private initializationOrder: string[] = [];

  /** Whether systems have been initialized */
  private initialized = false;

  /**
   * Register a game system.
   *
   * @param system - The system to register
   * @throws Error if system ID already exists or systems are initialized
   */
  register(system: GameSystem): void {
    if (this.initialized) {
      throw new Error(`Cannot register system "${system.id}": systems are already initialized`);
    }

    if (this.systems.has(system.id)) {
      throw new Error(`System "${system.id}" is already registered`);
    }

    this.systems.set(system.id, system);
  }

  /**
   * Get a system by ID.
   *
   * @param id - System ID
   * @returns The system, or undefined if not found
   */
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  get<T extends GameSystem>(id: string): T | undefined {
    return this.systems.get(id) as T | undefined;
  }

  /**
   * Check if a system is registered.
   *
   * @param id - System ID
   */
  has(id: string): boolean {
    return this.systems.has(id);
  }

  /**
   * Get all registered system IDs.
   */
  getSystemIds(): string[] {
    return Array.from(this.systems.keys());
  }

  /**
   * Initialize all systems in dependency order.
   *
   * @param context - Game context to pass to systems
   * @throws Error if there are circular dependencies or missing dependencies
   */
  async initializeAll(context: GameContext): Promise<void> {
    if (this.initialized) {
      throw new Error('Systems are already initialized');
    }

    // Build initialization order using topological sort
    this.initializationOrder = this.topologicalSort();

    // Initialize each system in order
    for (const systemId of this.initializationOrder) {
      const system = this.systems.get(systemId);
      if (system?.initialize) {
        await system.initialize(context);
      }
    }

    this.initialized = true;
  }

  /**
   * Run tick on all systems.
   *
   * @param context - Game context
   * @param deltaMilliseconds - Time since last tick
   */
  tick(context: GameContext, deltaMilliseconds: number): void {
    if (!this.initialized) {
      return;
    }

    for (const systemId of this.initializationOrder) {
      const system = this.systems.get(systemId);
      if (system?.tick) {
        system.tick(context, deltaMilliseconds);
      }
    }
  }

  /**
   * Dispose all systems in reverse initialization order.
   */
  dispose(): void {
    if (!this.initialized) {
      return;
    }

    // Dispose in reverse order
    const reversed = [...this.initializationOrder].reverse();
    for (const systemId of reversed) {
      const system = this.systems.get(systemId);
      if (system?.dispose) {
        system.dispose();
      }
    }

    this.initialized = false;
    this.initializationOrder = [];
  }

  /**
   * Whether systems have been initialized.
   */
  get isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Reset the registry completely.
   */
  reset(): void {
    this.dispose();
    this.systems.clear();
  }

  /**
   * Topological sort of systems based on dependencies.
   *
   * @returns Array of system IDs in initialization order
   * @throws Error if circular dependency or missing dependency detected
   */
  private topologicalSort(): string[] {
    const result: string[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();

    const visit = (id: string): void => {
      if (visited.has(id)) {
        return;
      }

      if (visiting.has(id)) {
        throw new Error(`Circular dependency detected involving system "${id}"`);
      }

      const system = this.systems.get(id);
      if (!system) {
        throw new Error(`System "${id}" not found`);
      }

      visiting.add(id);

      // Visit dependencies first
      for (const depId of system.dependencies ?? []) {
        if (!this.systems.has(depId)) {
          throw new Error(`System "${id}" depends on "${depId}" which is not registered`);
        }
        visit(depId);
      }

      visiting.delete(id);
      visited.add(id);
      result.push(id);
    };

    // Visit all systems
    for (const id of this.systems.keys()) {
      visit(id);
    }

    return result;
  }
}
